import React, { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { connect, useDispatch } from 'react-redux';

import PropTypes from 'prop-types';

import styled from 'styled-components';

import { InfoPage } from 'components/common/Layout/InfoPage/InfoPage';
import { CloudFlareError } from 'components/exceptions/CloudFlareError';
import ModalComponent from 'components/utils/ModalComponent';

import { toggleSpinner, toggleCloudFlareModal } from 'actions/appAction';

import { CAPTCHA_ERROR_SHOWN, CAPTCHA_SCREEN_SHOWN } from 'constants/amplitude/AmplitudeEvents';
import { CLOUD_FLARE_FAILED_MESSAGE, CLOUD_FLARE_SUCCESS_MESSAGE } from 'constants/Constants';
import { CLOUD_FLARE_VERIFICATION, HELP, PAYSEND_URL, SITE_PAYSEND } from 'constants/UrlConstants';

import { getLangCountryPrefix } from 'helpers/utils';

import amplitude from 'amplitude-js';
import { useApp, useLanguage } from 'hooks/selectors';

const Iframe = styled.iframe`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-width: 0;
`;

const ErrorScreen = () => {
    const { country } = useApp();
    const { lang } = useLanguage();
    const helpLink = `${getLangCountryPrefix(lang, country)}${HELP}`;

    return (
        <InfoPage
            dataTestId={'error-screen'}
            animatedIconFail
            headerText={<Trans>ps.500.title</Trans>}
            buttonRowType="link"
            buttonRowCaption={<Trans>ps.404.help</Trans>}
            buttonRowHref={helpLink}
            insideModalComponent
        />
    );
};

const CloudFlareModal = ({ toggleCloudFlareModal, toggleSpinner, processJsonFunc }) => {
    const [error, setError] = useState(false);
    const {
        cloudFlareModal: { path, options },
    } = useApp();
    const origin = __isBrowser__ ? window.location.origin : '';
    const dispatch = useDispatch();

    const isPaysendOrigin = (origin) => {
        try {
            const { hostname } = new URL(origin);
            return hostname === 'paysend.com' || hostname.endsWith('.paysend.com');
        } catch {
            return false;
        }
    };

    useEffect(() => {
        const onMessage = (event) => {
            if (!isPaysendOrigin(event.origin)) return;
            if (event.data === CLOUD_FLARE_SUCCESS_MESSAGE) {
                toggleSpinner(true);
                fetch(path, options)
                    .then((response) => {
                        if (response.status !== 497) {
                            return response.json();
                        } else {
                            setError(true);
                            throw new CloudFlareError(`Response status ${response.status}`);
                        }
                    })
                    .then((json) => {
                        toggleCloudFlareModal(false);
                        if (json.redirectTo) {
                            window.location.replace(json.redirectTo);
                        }
                        if (json) {
                            processJsonFunc?.(dispatch, json);
                        }
                    })
                    .catch((err) => {
                        console.warn(err);
                        setError(true);
                    })
                    .finally(() => toggleSpinner(false));
            }
            if (event.data === CLOUD_FLARE_FAILED_MESSAGE) {
                setError(true);
            }
        };
        if (__isBrowser__) {
            window.addEventListener('message', onMessage);
        }
        return () => {
            window.removeEventListener('message', onMessage);
        };
    }, [path, options, toggleCloudFlareModal, dispatch, toggleSpinner, processJsonFunc]);

    useEffect(() => {
        const eventName = error ? CAPTCHA_ERROR_SHOWN : CAPTCHA_SCREEN_SHOWN;
        amplitude.getInstance().logEvent(eventName);
    }, [error]);

    return (
        <ModalComponent canCloseModal={false} isVerticalCentered>
            {!error ? <Iframe src={`${origin}${CLOUD_FLARE_VERIFICATION}`} title="iframe" /> : <ErrorScreen />}
        </ModalComponent>
    );
};
CloudFlareModal.propTypes = {
    processJsonFunc: PropTypes.func,
};
export default connect(null, { toggleCloudFlareModal, toggleSpinner })(CloudFlareModal);
